import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import {connect} from 'react-redux';
import { TicketVistaCard } from '@app/components/index';
import TablaTicketService from '@app/services/tablaTicket.service';

const ITEMS_PER_PAGE = 15;

function TabsTicketDerivados({
    filtro,
    ticketrefresh,
    onCountChange,
    onAlertChange
}) {
    const [tickets, setTickets] = useState([]);
    const [filtroTK, setFiltroTK] = useState([]);
    const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
    const [busqueda, setBusqueda] = useState('');
    const [grupoFiltro, setGrupoFiltro] = useState('TODOS');
    const observerRef = useRef(null);
    const mountedRef = useRef(true);

    const lastItemRef = useCallback((node) => {
        if (observerRef.current) observerRef.current.disconnect();
        observerRef.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && visibleCount < filtroTK.length) {
                setVisibleCount((prev) => Math.min(prev + ITEMS_PER_PAGE, filtroTK.length));
            }
        });
        if (node) observerRef.current.observe(node);
    }, [visibleCount, filtroTK.length]);

    const actualizar = () =>{
        TablaTicketService.getAll({
            consulta: 'derivados',
            cvv: true
        })
        .then((response)=>{
            if (!mountedRef.current) return;
            setTickets(Array.isArray(response) ? response : []);
        })
        .catch(() => {
            if (!mountedRef.current) return;
            setTickets([]);
        })
    }

    // Extraer grupos únicos de los tickets cargados
    const gruposDisponibles = useMemo(() => {
        const grupos = tickets
            .map((tk) => tk?.grupoDerivado)
            .filter(Boolean);
        return [...new Set(grupos)].sort();
    }, [tickets]);

    useEffect(() => {
        let resultado = tickets;
        // Filtro por estado (prop filtro)
        if(filtro !== 'TODOS'){
            resultado = resultado.filter((tk) => tk?.ticket?.estado_tk === filtro);
        }
        // Filtro por grupo
        if(grupoFiltro !== 'TODOS'){
            resultado = resultado.filter((tk) => tk?.grupoDerivado === grupoFiltro);
        }
        // Filtro por búsqueda de texto (título o id)
        if(busqueda.trim() !== ''){
            const q = busqueda.trim().toLowerCase();
            resultado = resultado.filter((tk) =>
                tk?.ticket?.titulo_tk?.toLowerCase().includes(q) ||
                String(tk?.ticket?.id_tk).includes(q)
            );
        }
        setFiltroTK(resultado);
        if(onCountChange) onCountChange(resultado.length);
        if(onAlertChange) onAlertChange(resultado.some((tk) => ['VENCIDO','VENCEHOY','PORVENCER'].includes(tk?.ticket?.estado_tk)));
        setVisibleCount(ITEMS_PER_PAGE);
    }, [filtro, tickets, busqueda, grupoFiltro, onCountChange, onAlertChange])

    useEffect(() => {
        if(ticketrefresh?.reloadingGroup){
            actualizar();
        }
    }, [ticketrefresh])

    useEffect(() => {
        actualizar();
        return () => { mountedRef.current = false; };
    }, [])

    const visibleItems = filtroTK.slice(0, visibleCount);

    return (
        <div className="p-3 p-lg-5">
            {/* Barra de búsqueda y select de grupo */}
            <div className="row mb-3">
                <div className="col-12 col-md-6 mb-2 mb-md-0">
                    <div className="input-group" style={{height: '38px'}}>
                        <div className="input-group-prepend">
                            <span className="input-group-text h-100">
                                <i className="fas fa-search" />
                            </span>
                        </div>
                        <div style={{position: 'relative', flex: 1}}>
                            <input
                                type="text"
                                className="form-control h-100"
                                placeholder="Buscar por título o N° de ticket..."
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                                style={{paddingRight: busqueda ? '36px' : undefined}}
                            />
                            {busqueda && (
                                <button
                                    type="button"
                                    onClick={() => setBusqueda('')}
                                    style={{
                                        position: 'absolute',
                                        right: '8px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        background: 'none',
                                        border: 'none',
                                        padding: '0',
                                        color: '#dc3545',
                                        cursor: 'pointer',
                                        lineHeight: 1,
                                    }}
                                >
                                    <i className="fas fa-times" />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-4">
                    <div className="input-group" style={{height: '38px'}}>
                        <div className="input-group-prepend">
                            <span className="input-group-text h-100">
                                <i className="fas fa-users" />
                            </span>
                        </div>
                        <select
                            className="form-control h-100"
                            value={grupoFiltro}
                            onChange={(e) => setGrupoFiltro(e.target.value)}
                        >
                            <option value="TODOS">Todos los grupos</option>
                            {gruposDisponibles.map((g) => (
                                <option key={g} value={g}>{g}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="col-12 col-md-2 d-flex align-items-center">
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            backgroundColor: '#dc3545',
                            color: '#fff',
                            borderRadius: '20px',
                            padding: '4px 12px',
                            fontSize: '13px',
                            fontWeight: '600',
                        }}
                    >
                        <i className="fas fa-ticket-alt mr-1" />
                        {filtroTK.length}
                        {' ticket'}
                        {filtroTK.length !== 1 ? 's' : ''}
                    </span>
                </div>
            </div>
            <div className="row">
                <div className="offset-lg-12 col-lg-12">
                    {tickets && filtroTK?.length > 0 ? (
                        <>
                            {visibleItems.map((ticket, index) => {
                                const isLast = index === visibleItems.length - 1;
                                return (
                                    <div
                                        ref={isLast ? lastItemRef : null}
                                        key={`${ticket?.ticket?.id_tk}-${ticket?.ticket?.createdPersonaId_tk}`}
                                        className="mb-3"
                                    >
                                        {ticket?.grupoDerivado && (
                                            <span className="badge badge-info mb-1" style={{fontSize: '12px'}}>
                                                <i className="fas fa-users mr-1" />
                                                Derivado a:
                                                {' '}
                                                {ticket.grupoDerivado}
                                            </span>
                                        )}
                                        <TicketVistaCard
                                            item={{
                                                ...ticket?.ticket,
                                                ultimoComentario: ticket?.ultimoComentario,
                                                ultimoLog: ticket?.ultimoLog
                                            }}
                                            tratos={ticket?.tratos}
                                            className="mb-1"
                                            hideClickMap
                                        />
                                    </div>
                                )
                            })}
                            {visibleCount < filtroTK.length && (
                                <div className="text-center py-3">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="sr-only">
                                            Cargando...
                                        </span>
                                    </div>
                                </div>
                            )}
                        </>
                    ):(
                        <div className='alert alert-light mt-2 text-center'>
                            <i className="fas fa-exchange-alt" style={{fontSize: 200, color: 'gray'}}></i>
                            <div style={{fontSize: 20, color: 'gray'}}>
                                No hay tickets derivados a otros grupos
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const mapStateToProps = (state) => ({
    ticketrefresh: state.ticketrefresh
})

const mapDispatchToProps = (dispatch) => ({})

export default connect(mapStateToProps, mapDispatchToProps)(TabsTicketDerivados)
